import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PhArrowDownBoldSvg from '../../../assets/svg-fc/PhArrowDownBoldSvg';
import PhArrowsDownUpSvg from '../../../assets/svg-fc/PhArrowsDownUpSvg';
import PhArrowUpBoldSvg from '../../../assets/svg-fc/PhArrowUpBoldSvg';
import { UI_TOKENS } from '../../../constants/designTokens';
import { TableSkeleton } from './TableSkeleton';
import { SortDirection } from './TableToolbar';
import { DraggableColumns, orderColumnKeys, SortableHeaderCell } from '../draggable-columns/DraggableColumns';

export interface iDataTableColumn<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  sortValue?: (row: T) => string | number | Date | null | undefined;
  sortType?: 'text' | 'number' | 'date';
  searchValue?: (row: T) => string | null | undefined;
  /** Stays at the end, can't be dragged or hidden — e.g. a row's ⋯ menu. */
  fixed?: boolean;
}

interface iDataTableProps<T> {
  columns: iDataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  loading?: boolean;
  emptyMessage: string;
  /** The admin's saved column order; unknown or new columns fall in at their default place. */
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  hiddenColumns?: string[];
  pinnedColumns?: string[];
  sortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  rowIndicator?: (row: T) => boolean;
  minWidth?: number;
}

export const dataTableHeadSx = {
  padding: '12px 16px',
  fontFamily: UI_TOKENS.fontSans,
  fontWeight: 400,
  fontSize: 12.5,
  color: UI_TOKENS.textSecondary,
  whiteSpace: 'nowrap',
  bgcolor: UI_TOKENS.grey50,
  borderBottom: `1px solid ${UI_TOKENS.divider}`,
  position: 'sticky' as const,
  top: 0,
  zIndex: 1,
  '&:first-of-type': { pl: '20px' },
};

export const dataTableCellSx = {
  padding: '14px 16px',
  fontFamily: UI_TOKENS.fontSans,
  fontSize: 13.5,
  fontWeight: 400,
  color: UI_TOKENS.textStrong,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderBottom: 'none',
  '&:first-of-type': { pl: '20px' },
};

const DEFAULT_CELL_MAX_WIDTH = 240;
const DEFAULT_PIN_WIDTH = 160;
const INDICATOR_PADDING = '36px';

const SortIcon = ({ active, direction }: { active: boolean; direction?: SortDirection }) => (
  <Box
    component="span"
    sx={{ display: 'inline-flex', color: active ? UI_TOKENS.sage700 : UI_TOKENS.grey300 }}
  >
    {!active ? (
      <PhArrowsDownUpSvg size={12} />
    ) : direction === 'asc' ? (
      <PhArrowUpBoldSvg size={12} />
    ) : (
      <PhArrowDownBoldSvg size={12} />
    )}
  </Box>
);

const DataTable = <T,>({
  columns,
  rows,
  getRowId,
  loading = false,
  emptyMessage,
  columnOrder,
  onColumnOrderChange,
  hiddenColumns = [],
  pinnedColumns = [],
  sortKey,
  sortDirection,
  onSort,
  onRowClick,
  rowIndicator,
  minWidth = 960,
}: iDataTableProps<T>) => {
  if (loading) {
    return (
      <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableSkeleton rows={10} />
      </Box>
    );
  }

  const movable = columns.filter((column) => !column.fixed);
  const fullOrder = orderColumnKeys(
    columnOrder,
    movable.map((column) => column.key)
  );
  const shownKeys = fullOrder.filter((key) => !hiddenColumns.includes(key));
  const pinnedKeys = shownKeys.filter((key) => pinnedColumns.includes(key));
  const orderedKeys = shownKeys.filter((key) => !pinnedColumns.includes(key));
  /** Hidden columns keep their slots, so showing one again puts it back where it was. */
  const reorder = (visibleOrder: string[]) => {
    const queue = [...visibleOrder];
    onColumnOrderChange?.(fullOrder.map((key) => (orderedKeys.includes(key) ? (queue.shift() as string) : key)));
  };
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const visible = [
    ...pinnedKeys.map((key) => byKey[key]),
    ...orderedKeys.map((key) => byKey[key]),
    ...columns.filter((column) => column.fixed),
  ];
  const pinWidth = (column: iDataTableColumn<T>) => column.width ?? column.minWidth ?? DEFAULT_PIN_WIDTH;
  const pinOffsets: Record<string, number> = {};
  pinnedKeys.reduce((left, key) => {
    pinOffsets[key] = left;
    return left + pinWidth(byKey[key]);
  }, 0);
  const pinSx = (column: iDataTableColumn<T>, header: boolean) =>
    column.key in pinOffsets
      ? {
          position: 'sticky' as const,
          left: pinOffsets[column.key],
          zIndex: header ? 3 : 1,
          width: pinWidth(column),
          minWidth: pinWidth(column),
          maxWidth: pinWidth(column),
          bgcolor: header ? UI_TOKENS.grey50 : 'inherit',
        }
      : {};

  const headerLabel = (column: iDataTableColumn<T>) => {
    const active = column.key === sortKey;
    return column.sortValue && onSort ? (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {column.label}
        <SortIcon active={active} direction={sortDirection} />
      </Box>
    ) : (
      column.label
    );
  };

  const headerSx = (column: iDataTableColumn<T>) => ({
    ...dataTableHeadSx,
    textAlign: 'left' as const,
    width: column.width,
    minWidth: column.minWidth,
    ...(column.sortValue && onSort && { '&:hover': { color: UI_TOKENS.textStrong } }),
    ...pinSx(column, true),
    ...(rowIndicator && column.key === visible[0]?.key && { '&:first-of-type': { pl: INDICATOR_PADDING } }),
  });

  const sortHeader = (column: iDataTableColumn<T>) =>
    column.sortValue && onSort ? () => onSort(column.key) : undefined;

  const ariaSort = (column: iDataTableColumn<T>) =>
    column.sortValue && column.key === sortKey ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined;

  const table = (
    <Table sx={{ width: '100%', minWidth, borderCollapse: 'collapse' }}>
      <TableHead>
        <TableRow>
          {visible.map((column) =>
            column.fixed || !onColumnOrderChange || column.key in pinOffsets ? (
              <TableCell
                key={column.key}
                onClick={sortHeader(column)}
                aria-sort={ariaSort(column)}
                sx={{ ...headerSx(column), cursor: column.sortValue && onSort ? 'pointer' : 'default' }}
              >
                {headerLabel(column)}
              </TableCell>
            ) : (
              <SortableHeaderCell key={column.key} id={column.key} sx={headerSx(column)} onClick={sortHeader(column)}>
                {headerLabel(column)}
              </SortableHeaderCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={getRowId(row)}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={
              onRowClick
                ? (event) => {
                    if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
                    event.preventDefault();
                    onRowClick(row);
                  }
                : undefined
            }
            sx={{
              borderTop: `1px solid ${UI_TOKENS.divider}`,
              bgcolor: '#fff',
              ...(onRowClick && {
                cursor: 'pointer',
                '&:hover': { bgcolor: UI_TOKENS.surfaceHover },
                '&:focus-visible': { outline: `2px solid ${UI_TOKENS.sage600}`, outlineOffset: -2 },
              }),
            }}
          >
            {visible.map((column, index) => {
              const content = column.render(row);
              const leadsRow = Boolean(rowIndicator) && index === 0;
              const isText = typeof content === 'string' || typeof content === 'number';
              return (
                <TableCell
                  key={column.key}
                  title={isText ? String(content) : undefined}
                  sx={{
                    ...dataTableCellSx,
                    textAlign: 'left' as const,
                    maxWidth: column.maxWidth ?? DEFAULT_CELL_MAX_WIDTH,
                    ...pinSx(column, false),
                    ...(leadsRow && {
                      position: column.key in pinOffsets ? 'sticky' : 'relative',
                      '&:first-of-type': { pl: INDICATOR_PADDING },
                    }),
                  }}
                >
                  {leadsRow && rowIndicator?.(row) && (
                    <Box
                      component="span"
                      aria-label="Unread"
                      className="listChildTextBadge"
                      sx={{ position: 'absolute', left: 16, top: '50%', width: 8, height: 8, mt: '-4px' }}
                    />
                  )}
                  {content === '' || content === null || content === undefined ? '—' : content}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Box
        className="yc-scroll"
        sx={{
          flex: '1 0 auto',
          overflowX: 'auto',
          ...(rows.length === 0 && { display: 'flex', flexDirection: 'column', containerType: 'inline-size' }),
        }}
      >
        {onColumnOrderChange ? (
          <DraggableColumns keys={orderedKeys} onReorder={reorder}>
            {table}
          </DraggableColumns>
        ) : (
          table
        )}
        {rows.length === 0 && (
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              width: '100cqw',
              boxSizing: 'border-box',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 18px',
              textAlign: 'center',
              fontSize: 13,
              color: UI_TOKENS.textMuted,
            }}
          >
            {emptyMessage}
          </Box>
        )}
      </Box>
    </>
  );
};

export default DataTable;
